import React, { useState, useCallback, useRef, useEffect } from "react";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios, { CancelTokenSource } from "axios";

interface SearchBarProps {
  onSearch: (term: string, token: any) => void;
  reSearch: boolean;
  focus?: boolean;
  initTerm?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  reSearch,
  onSearch,
  focus = false,
  initTerm = "",
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  useEffect(() => {
    if (initTerm) {
      setSearchTerm(initTerm);
      debouncedFetchSearchResults(initTerm);
    }
  }, [])

  // Debounce function to limit the number of API calls
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let debounceTimer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func(...args), delay);
    };
  };

  // Function to fetch search results
  const fetchSearchResults = async (term: string) => {
    if (term) {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Operation canceled due to new request.");
      }
      cancelTokenRef.current = axios.CancelToken.source();

      await onSearch(term, cancelTokenRef.current.token)
    }
  };

  // Debounced version of fetchSearchResults
  // eslint-disable-next-line
  const debouncedFetchSearchResults = useCallback(
    debounce(fetchSearchResults, 800),
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);
    debouncedFetchSearchResults(term);
  };

  useEffect(() => {
    if (reSearch && searchTerm) {
      debouncedFetchSearchResults(searchTerm);
    }
    // eslint-disable-next-line
  }, [reSearch]);

  return (
    <>
      <TextField
        variant="outlined"
        fullWidth
        placeholder=""
        value={searchTerm}
        onChange={handleChange}
        autoFocus={focus}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          ),
          sx: {
            height: '40px',
            borderRadius: '20px',
          }
        }}
      />
    </>
  );
};

export default SearchBar;
